var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Howl } from 'howler';
import flatten from 'lodash/flatten';
import { isValidAudioSrc } from 'utils/audio-util';
import axios from 'axios';
import Vue from 'vue';
import { mapState } from 'vuex';
const MALE_SUFFIX = '-mt.';
const FEMALE_SUFFIX = '-ft.';
const media = {
    image: {
        constructor: Image,
        queue: 'imageQueue'
    },
    audio: {
        constructor: Howl,
        queue: 'audioQueue'
    },
    captions: {
        constructor: Object,
        queue: 'captionsQueue'
    }
};
export default Vue.extend({
    data() {
        return {
            imageQueue: [],
            audioQueue: [],
            captionsQueue: [],
            cache: new Map(),
            assetLoaderErrorLog: [],
            bundledAssetUrls: {
                puzzlePieces: {
                    innerShadowed: null,
                    plain: null
                }
            }
        };
    },
    computed: Object.assign({}, mapState('student', ['accessibilitySettings'])),
    methods: {
        notInCache(filename) {
            return !this.cache.has(filename);
        },
        addToQueue(assets, queueName, skipCache = false) {
            if (!assets || !queueName) {
                return;
            }
            const assetsDistinct = assets
                .filter(asset => asset !== undefined &&
                asset !== null &&
                (Array.isArray(asset) ? asset.find(x => x !== undefined && x !== null) : true))
                .map(asset => Array.isArray(asset)
                ? [...new Set(asset.filter(x => x !== undefined && x !== null))]
                : asset);
            if (skipCache) {
                this[queueName] = this[queueName].concat(...assetsDistinct);
                return;
            }
            this[queueName] = this[queueName].concat(...assetsDistinct.filter(this.notInCache));
        },
        queueImage(...imageAssetPaths) {
            this.addToQueue(imageAssetPaths, media.image.queue);
        },
        queueAudio(...audioAssetPaths) {
            this.addToQueue(audioAssetPaths, media.audio.queue);
        },
        queueAudioNoCache(...audioAssetPaths) {
            this.addToQueue(audioAssetPaths, media.audio.queue, true);
        },
        queueCaptions(...captionAssetPaths) {
            this.addToQueue(captionAssetPaths, media.captions.queue);
        },
        addToCache(src, asset) {
            this.cache.set(src, asset);
        },
        getVolume() {
            return this.accessibilitySettings.audioSettings.muteSounds
                ? 0
                : this.accessibilitySettings.audioSettings.volume * 0.01;
        },
        retryLoadAudio(src, configFn) {
            const newSrcName = src.includes(FEMALE_SUFFIX)
                ? src.replace(FEMALE_SUFFIX, MALE_SUFFIX)
                : src.replace(MALE_SUFFIX, FEMALE_SUFFIX);
            return new Promise(resolve => {
                const audio = new Howl(Object.assign(Object.assign({ src: newSrcName, volume: this.getVolume() }, configFn(src)), { onload: () => resolve(this.addToCache(src, audio)), onloaderror: () => resolve(this.addToCache(src, {
                        play: () => { },
                        stop: () => { }
                    })) }));
            });
        },
        loadAudio(configFn) {
            return (src = '') => new Promise(resolve => {
                // eslint-disable-line
                if (typeof src !== 'string' || !isValidAudioSrc(src)) {
                    return resolve(this.addToCache(src, {
                        play: () => { },
                        stop: () => { }
                    }));
                }
                const audio = new Howl(Object.assign(Object.assign({ src }, configFn(src)), { volume: this.getVolume(), onload: () => resolve(this.addToCache(src, audio)), onloaderror: () => __awaiter(this, void 0, void 0, function* () { return resolve(yield this.retryLoadAudio(src, configFn)); }) }));
            });
        },
        getExtraSoundPathObject(extraSoundPaths, audioCache) {
            const soundPathObject = {};
            extraSoundPaths.forEach(soundPath => {
                soundPathObject[soundPath] = audioCache.get(soundPath);
            });
            return soundPathObject;
        },
        getActivityRoundLetters(activityRounds, s3prefix, activityName, numberPrefix = '', voiceType = 'ma', audioKey = 'answer', extraSoundPaths = []) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPathsPerRound = activityRounds.map(round => round.steps.map(stepState => {
                    const formatText = stepState[audioKey].toLowerCase();
                    return `${s3prefix}/${activityName}-${formatText}${numberPrefix}-${voiceType}.mp3`;
                }));
                this.queueAudio(...flatten(soundPathsPerRound));
                extraSoundPaths.forEach(soundPath => this.queueAudio(soundPath));
                const cache = yield this.downloadAudio();
                return Object.assign({ soundsPerRound: soundPathsPerRound.map(roundSoundPaths => roundSoundPaths.map(stepSoundPath => cache.get(stepSoundPath))) }, this.getExtraSoundPathObject(extraSoundPaths, cache));
            });
        },
        getActivityRoundSounds(activityRounds, s3prefix, voiceType = 'ft', audioKey = 'answer', extraSoundPaths = []) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPathsPerRound = activityRounds.map(round => round.steps.map(stepState => {
                    let formatText = stepState[audioKey].replace(/ /g, '-').toLowerCase();
                    if (!formatText.endsWith('-')) {
                        formatText = `${formatText}-`;
                    }
                    return `${s3prefix}/${formatText.trim()}${voiceType}.mp3`;
                }));
                this.queueAudioNoCache(...flatten(soundPathsPerRound));
                extraSoundPaths.forEach(soundPath => this.queueAudioNoCache(soundPath));
                const cache = yield this.downloadAudio();
                return Object.assign({ soundsPerRound: soundPathsPerRound.map(roundSoundPaths => roundSoundPaths.map(stepSoundPath => cache.get(stepSoundPath))) }, this.getExtraSoundPathObject(extraSoundPaths, cache));
            });
        },
        loadImage(src) {
            return new Promise(resolve => {
                const image = new Image();
                image.onload = () => resolve(this.addToCache(src, image));
                image.onerror = () => resolve(image);
                image.src = src;
            });
        },
        loadCaption(src) {
            return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                const caption = new Array();
                yield axios
                    .get(src)
                    .then(response => {
                    response.data.cues.forEach(cue => {
                        caption.push(cue);
                    });
                })
                    .catch(error => { });
                resolve(this.addToCache(src, caption));
            }));
        },
        downloadAssets(queueName, loader) {
            return __awaiter(this, void 0, void 0, function* () {
                yield Promise.all(this[queueName].map(loader));
                this[queueName] = [];
                return this.cache;
            });
        },
        downloadImages() {
            return this.downloadAssets(media.image.queue, this.loadImage);
        },
        downloadCaptions() {
            return this.downloadAssets(media.captions.queue, this.loadCaption);
        },
        downloadAudio(isIos = false) {
            const audioLoader = this.loadAudio(this.getAudioConfig(isIos));
            return this.downloadAssets(media.audio.queue, audioLoader);
        },
        downloadAll({ isIos = false } = {}) {
            return __awaiter(this, void 0, void 0, function* () {
                yield Promise.all([
                    this.downloadImages(),
                    this.downloadAudio(isIos),
                    this.downloadCaptions()
                ]);
                return Promise.resolve(this.cache);
            });
        },
        loadAssets(...loadFunctions) {
            return Promise.all(loadFunctions.map(loader => loader()));
        },
        getAsset(path) {
            return this.cache.get(path);
        },
        sanitizePath(cleanMe) {
            // remove spaces in filenames
            const filename = cleanMe.split('/').pop();
            const path = cleanMe.replace(filename, '');
            const cleanedFileName = filename.replace(/\s+/g, '-');
            return path + cleanedFileName;
        },
        // only use this one for audio
        getFileName(cleanMe) {
            // remove path
            const filename = cleanMe.split('/').pop();
            // spaces to dash
            let cleanedFileName = filename.replace(/\s+/g, '-');
            // underscore to dash
            cleanedFileName = filename.replace('_', '-');
            // remove extension
            cleanedFileName = cleanedFileName.replace(/\.[^/.]+$/, '');
            // numbers
            cleanedFileName = cleanedFileName.replace(/[0-9]/g, '');
            // return as lowercase
            return cleanedFileName.toLowerCase();
        },
        getAudioConfig(isIos) {
            return path => {
                const isMusicClip = path.indexOf('default/files/mos1/aud') >= 0;
                const useHtml5Audio = isIos && isMusicClip;
                return useHtml5Audio
                    ? {
                        html5: true
                    }
                    : {
                        preload: true
                    };
            };
        },
        updateAllAudioVolumeInCache() {
            this.cache.forEach(cache => {
                var _a;
                (_a = cache === null || cache === void 0 ? void 0 : cache.volume) === null || _a === void 0 ? void 0 : _a.call(cache, this.getVolume());
            });
        }
    }
});
